import React, { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTag, faPen, faCheck, faTimes, faSpinner, faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';
import useToken from 'hooks/useToken';
import { useToast } from 'context/providers/ToastProvider';

const TicketMobileSubHeader = ({
  ticket,
  onToggleMobileSidebar,
  onTicketUpdated
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const { token } = useToken();
  const { showSuccessToast, showErrorToast } = useToast();

  const [isEditingMotivo, setIsEditingMotivo] = useState(false);
  const [motivoText, setMotivoText] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  const ticketId = ticket?.idTicket || ticket?.id;
  const motivoActual = ticket?.vAsunto || ticket?.subject || '';

  useEffect(() => {
    if (!isEditingMotivo) {
      setMotivoText(motivoActual);
    }
  }, [motivoActual, ticketId, isEditingMotivo]);

  useEffect(() => {
    if (isEditingMotivo && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditingMotivo]);

  const handleStartEdit = () => {
    setMotivoText(motivoActual);
    setIsEditingMotivo(true);
  };

  const handleCancelEdit = () => {
    setMotivoText(motivoActual);
    setIsEditingMotivo(false);
  };

  const handleSaveMotivo = async e => {
    if (e) e.preventDefault();
    if (!ticketId || isSaving) return;

    const trimmed = motivoText.trim();
    if (trimmed === motivoActual) {
      setIsEditingMotivo(false);
      return;
    }

    setIsSaving(true);
    try {
      const response = await fetch('/api/SupportEngine/UpdateTicketSubject', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: 'Bearer ' + token
        },
        body: JSON.stringify({
          idTicket: parseInt(ticketId, 10),
          subject: trimmed
        })
      });

      if (response.ok) {
        if (onTicketUpdated) {
          onTicketUpdated({ vAsunto: trimmed });
        }
        setIsEditingMotivo(false);
        if (showSuccessToast) {
          showSuccessToast('Motivo actualizado', 'El motivo del ticket se guardó correctamente.');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        if (showErrorToast) {
          showErrorToast('Error', errorData.message || 'No se pudo actualizar el motivo.');
        }
      }
    } catch (err) {
      if (showErrorToast) {
        showErrorToast('Error de conexión', err.message || 'Ocurrió un error al guardar.');
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div
      className="d-xl-none px-3 py-2 border-bottom d-flex align-items-center gap-2 flex-shrink-0"
      style={{
        backgroundColor: isDark ? '#121e2d' : '#f8fafc',
        borderColor: isDark ? '#233142' : '#e2e8f0',
        minHeight: '44px'
      }}
    >
      {/* Botón Detalles en píldora con borde azul suave (lado izquierdo) */}
      {onToggleMobileSidebar && (
        <button
          type="button"
          onClick={onToggleMobileSidebar}
          className="btn btn-sm px-3 py-1 rounded-pill d-inline-flex align-items-center gap-2 shadow-none flex-shrink-0"
          style={{
            backgroundColor: isDark ? 'rgba(56, 189, 248, 0.12)' : '#ffffff',
            color: isDark ? '#38bdf8' : '#0284c7',
            border: isDark ? '1px solid rgba(56, 189, 248, 0.35)' : '1px solid #bfdbfe',
            fontSize: '0.80rem',
            fontWeight: 600
          }}
          title="Ver detalles del ticket"
        >
          <FontAwesomeIcon icon={faInfoCircle} style={{ fontSize: '0.78rem' }} />
          <span>Detalles</span>
        </button>
      )}

      {/* Motivo del ticket (vista compacta / edición al lado de Detalles) */}
      <div className="flex-grow-1 overflow-hidden" style={{ minWidth: 0 }}>
        {isEditingMotivo ? (
          <form onSubmit={handleSaveMotivo} className="d-flex align-items-center gap-1 w-100">
            <input
              ref={inputRef}
              type="text"
              className="form-control form-control-sm rounded-pill px-3"
              placeholder="Motivo del ticket..."
              value={motivoText}
              onChange={e => setMotivoText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Escape') {
                  e.preventDefault();
                  handleCancelEdit();
                }
              }}
              disabled={isSaving}
              maxLength={250}
              style={{
                fontSize: '0.80rem',
                backgroundColor: isDark ? '#1e293b' : '#ffffff',
                color: isDark ? '#f8fafc' : '#0f172a',
                borderColor: isDark ? '#3b82f6' : '#93c5fd'
              }}
            />
            <button
              type="submit"
              className="btn btn-sm btn-primary d-flex align-items-center justify-content-center p-0 rounded-circle flex-shrink-0"
              style={{ width: '28px', height: '28px', minWidth: '28px' }}
              title="Guardar motivo"
              disabled={isSaving}
            >
              {isSaving ? (
                <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: '0.70rem' }} />
              ) : (
                <FontAwesomeIcon icon={faCheck} style={{ fontSize: '0.70rem' }} />
              )}
            </button>
            <button
              type="button"
              onClick={handleCancelEdit}
              className="btn btn-sm d-flex align-items-center justify-content-center p-0 rounded-circle shadow-none flex-shrink-0"
              style={{
                width: '28px',
                height: '28px',
                minWidth: '28px',
                backgroundColor: isDark ? '#1e293b' : '#f8fafc',
                border: isDark ? '1px solid #233142' : '1px solid #e2e8f0',
                color: isDark ? '#94a3b8' : '#64748b'
              }}
              title="Cancelar"
              disabled={isSaving}
            >
              <FontAwesomeIcon icon={faTimes} style={{ fontSize: '0.70rem' }} />
            </button>
          </form>
        ) : (
          <div
            className="d-inline-flex align-items-center gap-1.5 px-3 py-1 rounded-pill shadow-none"
            style={{
              cursor: 'pointer',
              maxWidth: '100%',
              backgroundColor: isDark ? '#1e293b' : '#ffffff',
              border: isDark ? '1px solid #334155' : '1px solid #cbd5e1',
              transition: 'all 0.15s ease'
            }}
            onClick={handleStartEdit}
            title="Clic para editar el motivo del ticket"
            role="button"
            tabIndex={0}
          >
            <FontAwesomeIcon
              icon={faTag}
              style={{ fontSize: '0.70rem', color: motivoActual ? '#3b82f6' : (isDark ? '#64748b' : '#94a3b8') }}
            />
            <span
              className={`text-truncate ${motivoActual ? 'fw-medium' : 'fst-italic'}`}
              style={{
                fontSize: '0.80rem',
                color: motivoActual ? (isDark ? '#e2e8f0' : '#475569') : (isDark ? '#94a3b8' : '#64748b'),
                maxWidth: '220px'
              }}
            >
              {motivoActual || '+ Agregar motivo'}
            </span>
            <FontAwesomeIcon
              icon={faPen}
              style={{ fontSize: '0.66rem', color: isDark ? '#94a3b8' : '#64748b', opacity: 0.8 }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

TicketMobileSubHeader.propTypes = {
  ticket: PropTypes.object,
  onToggleMobileSidebar: PropTypes.func,
  onTicketUpdated: PropTypes.func
};

export default TicketMobileSubHeader;
